import { Container, Row, Col, Button, Form } from "react-bootstrap"
import { useFormik, FormikProvider, Field, Form as FormikForm } from "formik"
import * as Yup from "yup"
import styles from "./AccountManagement.module.scss"
import { getStudentAccountInfo, resetStudentAccountPassword, updatePageAccess } from "shared/routes/account/account-management"
import { BeatLoader } from "react-spinners"
import { toast } from "react-toastify"
import { useEffect, useState } from "react"
import { StudentAccountOptionsReturn, StudentPageAccessOptions } from "shared/types/accountManagementTypes"

const UpdateStudentAccountInfoPage = () => {
    const [studentAccountInfo, setStudentAccountInfo] = useState<StudentAccountOptionsReturn>(null)

    const [loading, setLoading] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [pageAccessValues, setPageAccessValues] = useState<StudentPageAccessOptions>({
        hasBackgroundAccess: false,
        hasMethodsAccess: false,
        hasAssesmentAccess: false,
    })

    useEffect(() => {
        getStudentAccountInfo()
        .then(info => {
            setStudentAccountInfo(info)
            setPageAccessValues({
                hasBackgroundAccess: info.hasBackgroundAccess,
                hasMethodsAccess: info.hasMethodsAccess,
                hasAssesmentAccess: info.hasAssesmentAccess,
            })
        })
        .catch((err) => {
            toast.error(err.response?.data ?? "There has been an error getting the student account information.")
        })
        .finally(() => setLoading(false))
    }, [])

    const handleResetPassword = (values: {currentTeacherPassword: string, newStudentPassword: string, confirmStudentPassword: string}) => {
        setIsSubmitting(true)
        resetStudentAccountPassword(values)
        .then(() => {
            toast.success("Student account password has been successfully updated.")
        })
        .catch(err => {
            toast.error(err.response?.data ?? "There has been an error updating the student account password.")
        })
        .finally(() => {
            setIsSubmitting(false)
            formik.resetForm()
        })
    }

    const handleUpdatePageAccess = () => {
        setIsSubmitting(true)
        updatePageAccess(pageAccessValues)
        .then(() => {
            toast.success("Student account access has been successfully updated.")
        })
        .catch(err => {
            toast.error(err.response?.data ?? "There has been an error updating the student account access.")
        })
        .finally(() => {
            setIsSubmitting(false)
            formik.resetForm()
        })
    }

    const formik = useFormik({
        initialValues: {
            currentTeacherPassword: "",
            newStudentPassword: "",
            confirmStudentPassword: "",
        },
        onSubmit: handleResetPassword,
        enableReinitialize: true,
        validationSchema: Yup
            .object()
            .shape({
                currentTeacherPassword: Yup
                    .string()
                    .label("Current Teacher Password")
                    .required("Current Teacher Password is required."),
                newStudentPassword: Yup
                    .string()
                    .label("New Student Password")
                    .min(5, "Password must be 5 characters long")
                    .max(150)
                    .required("New Student Password is required."),
                confirmStudentPassword: Yup
                    .string()
                    .label("Confirm Student Password")
                    .min(5, "Password must be 5 characters long")
                    .max(150)
                    .oneOf([Yup.ref("newStudentPassword"), null], "Password you confirm need to match with new student password.")
                    .required("Confirm Student Password is required."),
            })
    })

    if (loading || isSubmitting) return <BeatLoader />

  return (
    <Container className="mt-4">
        <Row className="text-center">
            <h4>Update Student Account Information</h4>
        </Row>
        <Row className="my-4">
            <h5 className="border border-secondary rounded p-3">Student Account Username: <span className="text-primary">{studentAccountInfo.studentUsername}</span></h5>
        </Row>
        <Row className="mt-4">
            <Col sm={12} lg={6}>
                <h3 className="text-start">Reset Password</h3>
                <p className="text-start text-secondary">Reset the password for the student account (that all of your students use).</p>
                <FormikProvider value={formik}>
                    <Form as={FormikForm} className={`text-center border border-1 border-secondary rounded p-3`}>
                        <Form.Group controlId="formCurrentTeacherPassword" className="my-4" as={Col}>
                            <Form.Label className={`${styles.label} d-block text-muted`}>Current Teacher Password</Form.Label>
                            <Form.Control
                                className={`${styles.input}`}
                                name="currentTeacherPassword"
                                type="password"
                                placeholder="Current Teacher Password"
                                as={Field}
                                onChange={formik.handleChange}
                                value={formik.values.currentTeacherPassword}
                                isInvalid={formik.touched.currentTeacherPassword && !!formik.errors.currentTeacherPassword}
                            />
                            <Form.Control.Feedback type="invalid" className="text-start">
                                {formik.errors.currentTeacherPassword}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId="formNewStudentPassword" className="my-4" as={Col}>
                            <Form.Label className={`${styles.label} d-block text-muted`}>New Student Password</Form.Label>
                            <Form.Control
                                className={`${styles.input}`}
                                name="newStudentPassword"
                                type="password"
                                placeholder="New Student Password"
                                as={Field}
                                onChange={formik.handleChange}
                                value={formik.values.newStudentPassword}
                                isInvalid={formik.touched.newStudentPassword && !!formik.errors.newStudentPassword}
                            />
                            <Form.Control.Feedback type="invalid" className="text-start">
                                {formik.errors.newStudentPassword}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId="formConfirmNewStudentPassword" className="my-4" as={Col}>
                            <Form.Label className={`${styles.label} d-block text-muted`}>Confirm Student Password</Form.Label>
                            <Form.Control
                                className={`${styles.input}`}
                                name="confirmStudentPassword"
                                type="password"
                                placeholder="Confirm Student Password"
                                as={Field}
                                onChange={formik.handleChange}
                                value={formik.values.confirmStudentPassword}
                                isInvalid={formik.touched.confirmStudentPassword && !!formik.errors.confirmStudentPassword}
                            />
                            <Form.Control.Feedback type="invalid" className="text-start">
                                {formik.errors.confirmStudentPassword}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Button
                            className="rounded-lg px-4 py-2 mt-4"
                            variant="theme"
                            disabled={!formik.dirty || !formik.isValid || isSubmitting}
                            type="submit">
                                Change Password
                        </Button>
                    </Form>
                </FormikProvider>
            </Col>
            <Col sm={12} lg={6}>
                <h3 className="text-start">Page Access</h3>
                <p className="text-start text-secondary">Limit access to certain sections of the simulation page for all of your students.</p>
                <div className="text-start">
                    <Form>
                        <Form.Check
                            type="switch"
                            id="background-switch"
                            label="Access to Background section"
                            checked={pageAccessValues.hasBackgroundAccess}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPageAccessValues(prev => ({...prev, hasBackgroundAccess: e.target.checked}))}
                        />
                        <Form.Check
                            className="mt-3"
                            type="switch"
                            id="methods-switch"
                            label="Access to Methods section"
                            checked={pageAccessValues.hasMethodsAccess}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPageAccessValues(prev => ({...prev, hasMethodsAccess: e.target.checked}))}
                        />
                        <Form.Check
                            className="mt-3"
                            type="switch"
                            id="assessment-switch"
                            label="Access to Assessment section"
                            checked={pageAccessValues.hasAssesmentAccess}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPageAccessValues(prev => ({...prev, hasAssesmentAccess: e.target.checked}))}
                        />
                        <Button
                            className="rounded-lg px-4 py-2 mt-4"
                            variant="theme"
                            type="submit"
                            onClick={handleUpdatePageAccess}>
                                Save Access Changes
                        </Button>
                    </Form>
                </div>
            </Col>
        </Row>
    </Container>
  )
}

export default UpdateStudentAccountInfoPage
